import { IonHeader, IonIcon, IonPage, IonTitle } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { LANGUAGES } from "@/i18n";
import { type LanguagePreference, useLanguage } from "@/lib/hooks/use-language";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { checkIcon } from "@/lib/icons/check.icon";
import { Routes } from "@/lib/router";
import { cx } from "@/lib/style/cva.config";

const OPTIONS = ["system", ...LANGUAGES] as const satisfies readonly LanguagePreference[];

export function SettingsLanguagePage() {
  const { t } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();
  const { preference, system, setPreference } = useLanguage();

  function choose(language: LanguagePreference) {
    if (language === preference) {
      return;
    }

    // Same order as the appearance picker: the switch lands first and the feedback is
    // fire-and-forget, because Capacitor's web haptics throw where `navigator.vibrate` does not
    // exist and a preference that fails to save on those platforms would be a strange way to lose
    // this.
    setPreference(language);
    PreferredHaptics.impact();
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <Buttons slot="secondary">
            <BackButton defaultHref={Routes.Settings()} />
          </Buttons>
          <IonTitle>{t("common:language.name")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content scrollUnderChrome>
        <div className="flex flex-1 flex-col gap-6 p-4">
          <p className="text-sm text-txt-secondary">{t("settings.language.intro")}</p>

          <div
            role="radiogroup"
            aria-label={t("common:language.name")}
            className="flex flex-col overflow-hidden rounded-xl border border-bordercolor-primary"
          >
            {OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={preference === option}
                onClick={() => choose(option)}
                className="flex items-center justify-between gap-3 border-b border-bordercolor-primary px-4 py-3 text-left last:border-b-0"
              >
                <span
                  className={cx(
                    "text-base",
                    preference === option ? "font-semibold text-txt-primary" : "text-txt-secondary",
                  )}
                >
                  {t(`common:language.options.${option}`)}
                </span>
                {preference === option && (
                  <IonIcon icon={checkIcon} aria-hidden className="size-5 text-txt-brand" />
                )}
              </button>
            ))}
          </div>

          {/*
            "System language" on its own does not say which language that is, and the employee
            picking it is often the one least sure. Naming the resolved language is the whole point
            of showing this line.
          */}
          {preference === "system" && (
            <p className="text-sm text-txt-tertiary">
              {t("settings.language.following", {
                language: t(`common:language.options.${system}`).toLowerCase(),
              })}
            </p>
          )}
        </div>
      </Content>
    </IonPage>
  );
}
